import {
  OnboardingStepType,
  OrganizationDetailFragment,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import {
  alpha,
  Box,
  Card,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useOrgQuery } from '../organization/useOrg'
import { determineCanUsePaygroupBilling, hasStartedBillingConnection } from './billingUtils'
import { useSetUnifiedBilling } from './useSetUnifiedBilling'

export const UnifiedBillingForm = ({ org }: { org: OrganizationDetailFragment }) => {
  const { isFetching: isRefreshingOrg } = useOrgQuery()
  const { mutate: setUnifiedBilling, isLoading: isSaving } = useSetUnifiedBilling([
    useOrgQuery.getKey({ orgId: org.id }),
  ])
  const { mutate: startOnboardingStep } = useStartOnboardingStep(org.id, {
    onSuccess: () => startOnboardingStep({ stepType: OnboardingStepType.ConnectBilling }),
  })
  const isUpdatingOrg = isSaving || isRefreshingOrg

  const canUsePaygroupBilling = determineCanUsePaygroupBilling(org)

  if (!org || !canUsePaygroupBilling) return null
  if (hasStartedBillingConnection(org)) {
    if (org.billing?.hasUnifiedBilling) {
      return (
        <Typography variant="body2" gutterBottom>
          You&apos;ve chosen to bill all pay groups from the same account
        </Typography>
      )
    }
    return (
      <Typography variant="body2" gutterBottom>
        You&apos;ve chosen to connect a billing method for <strong>each pay group</strong>.
      </Typography>
    )
  }

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) =>
    setUnifiedBilling({ orgId: org.id, hasUnifiedBilling: value === 'true' })

  const currentUnifiedBillingValue = org.billing?.hasUnifiedBilling
  const disabled = isUpdatingOrg

  return (
    <Box mb={2}>
      <Typography variant="body2" gutterBottom>
        We support billing at the <strong>organization</strong> level or <strong>per-pay-group</strong>.
      </Typography>

      <Box mb={2}>
        <Grid
          container
          spacing={2}
          component={RadioGroup}
          value={currentUnifiedBillingValue}
          onChange={handleChange}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              component={FormControlLabel}
              disabled={disabled}
              value={true}
              control={<Radio />}
              label="All pay groups are billed from one account"
              sx={(theme) => ({
                p: theme.spacing(1.5, 1, 2),
                m: 0,
                backgroundColor: currentUnifiedBillingValue
                  ? alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity)
                  : undefined,
                borderColor: currentUnifiedBillingValue ? theme.palette.primary.main : undefined,
              })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              component={FormControlLabel}
              disabled={disabled}
              value={false}
              control={<Radio />}
              label="Each pay group should be billed separately"
              sx={(theme) => ({
                p: theme.spacing(1.5, 1, 2),
                m: 0,
                backgroundColor: currentUnifiedBillingValue
                  ? undefined
                  : alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                borderColor: currentUnifiedBillingValue ? undefined : theme.palette.primary.main,
              })}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
